import { useEffect, useState, useMemo } from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { Tabs, Checkbox } from "antd"
import { IoIosNotifications } from "react-icons/io"

import ArrowBackButton from "../../../components/button/ArrowBackButton"
import DeleteModal from "../../../components/modal/DeleteModal"
import Loading from "../../../components/loading/Loading"

import PhaseDeadline from "./component/PhaseDeadline"
import ActualScore from "./component/ActualScore"
import Calibrator from "./component/Calibrator"
import RatingQuota from "./component/RatingQuota"
import useProjectDetail from "./useProjectDetail"
import ActionButtonGradient from "../../../components/button/ActionButtonGradient"

const ProjectDetail = () => {
    // ============= HOOKS =============
    const navigate = useNavigate()
    const location = useLocation()
    const params = useParams()

    const {
        payload,
        getProjectForm,
        ratingQuotas,
        getRatingQuota,
        users,
        getUsersData,
        pageRatingQuota,
        totalPageRatingQuota,
        changePageRatingQuota,
        pageUsers,
        totalPageUsers,
        changePageUsers,
        publishProject,
        deactivateProject,
        setDataCalibration,
        userCalibration,
        notifyCalibrator,
        deleteRatingQuota,
        deleteCalibrationData,
        loadingPagestate,
        setFilterNameUserRatingQuota,
        setFilterLimitRatingQuota,
        setFilterNameUsers,
        limitUsers,
        filterName,
        setFilterLimitUsers,
        divisions,
        getDivisions,
        isSendNotification,
        setFilterDivisionUsers
    } = useProjectDetail()

    // ============= LOCAL STATE =============
    const [show, setShow] = useState(false)
    const [deleteID, setDeleteID] = useState()
    const [deleteTitleName, setName] = useState()
    const [isRatingQuota, setIsRatingQuota] = useState()
    const [activeTabs, setActiveTabs] = useState('1')

    // ============= HANDLERS =============
    const handleClose = () => setShow(false)
    const handleShow = () => setShow(true)

    const handleTabChange = (key) => {
        const basePath = location.pathname.substring(0, location.pathname.lastIndexOf('/'))
        const newPath = `${basePath}/${key}`
        navigate(newPath)
    }

    // ============= MEMOIZED VALUES =============
    const tabs = useMemo(() => [
        {
            key: '1',
            label: 'Phase Configuration',
            children: <PhaseDeadline 
                project_id={params.project_id} 
                phases={payload.ProjectPhases}
            />
        },
        {
            key: '2',
            label: 'Rating Quota',
            children: <RatingQuota 
                active={payload.Active}
                project_id={params.project_id}
                payload={payload}
                ratingQuotas={ratingQuotas}
                handleShow={handleShow}
                setDeleteID={setDeleteID}
                setName={setName}
                setIsRatingQuota={setIsRatingQuota}
                pageRatingQuota={pageRatingQuota}
                totalPageRatingQuota={totalPageRatingQuota}
                changePageRatingQuota={changePageRatingQuota}
                setFilterNameUserRatingQuota={setFilterNameUserRatingQuota}
                setFilterLimitRatingQuota={setFilterLimitRatingQuota}
                onRefresh={() => getRatingQuota(params.project_id, pageRatingQuota, '')}
            />
        },
        {
            key: '3',
            label: 'Actual Score',
            children: <ActualScore 
                active={payload.Active}
                project_id={params.project_id}
                payload={payload}
                users={users}
                userCalibration={userCalibration} 
                pageUsers={pageUsers}
                totalPageUsers={totalPageUsers}
                changePageUsers={changePageUsers}
                handleShow={handleShow}
                setDeleteID={setDeleteID}
                setName={setName}
                setIsRatingQuota={setIsRatingQuota}
                setFilterNameUsers={setFilterNameUsers}
                setFilterLimitUsers={setFilterLimitUsers}
                limitUsers={limitUsers}
                filterName={filterName}
                divisions={divisions}
                setFilterDivisionUsers={setFilterDivisionUsers}
                onRefresh={() => getUsersData(params.project_id)}
            />
        },
        {
            key: '4',
            label: 'Calibrator',
            children: <Calibrator
                active={payload.Active}
                project_id={params.project_id}
                payload={payload}
                users={users}
                userCalibration={userCalibration} 
                pageUsers={pageUsers}
                totalPageUsers={totalPageUsers}
                changePageUsers={changePageUsers}
                handleShow={handleShow}
                setDeleteID={setDeleteID}
                setName={setName}
                setIsRatingQuota={setIsRatingQuota}
                setFilterNameUsers={setFilterNameUsers}
                setFilterLimitUsers={setFilterLimitUsers}
                limitUsers={limitUsers}
                filterName={filterName}
                divisions={divisions}
                setFilterDivisionUsers={setFilterDivisionUsers}
                onRefresh={() => getUsersData(params.project_id)}
            />
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [payload, ratingQuotas, users, userCalibration, pageRatingQuota, totalPageRatingQuota, 
        pageUsers, totalPageUsers, limitUsers, filterName, divisions, params.project_id])

    // ============= EFFECTS =============
    useEffect(() => {
        if (params.project_id) {
            getProjectForm(params.project_id)
            getRatingQuota(params.project_id, 1, '')
        }
    }, [params.project_id, getProjectForm, getRatingQuota])

    useEffect(() => {
        if (payload && users) {
            setDataCalibration()
        }
    }, [payload, users, setDataCalibration])

    useEffect(() => {
        setActiveTabs(params.tab_order)

        if ((params.tab_order === '3' || params.tab_order === '4') && params.project_id) {
            getDivisions(params.project_id)
        }
    }, [params.tab_order, params.project_id, getDivisions])

    return(
        <div className="container-fluid justify-content-center" style={{ 
            maxWidth: '1200px', 
            margin: '0 auto',
            width: '100%',
        }}>
            <div style={{ marginBottom: '24px' }}>
                <ArrowBackButton title={"Project Detail"} path={'/project-list'} isTwoSection={true}/>
            </div>
            
            <div style={{
                backgroundColor: '#ffffff',
                borderRadius: '12px',
                padding: '24px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                border: '1px solid #f0f0f0'
            }}>
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h5 style={{ 
                        margin: 0,
                        fontWeight: 600,
                        color: '#1a1a1a',
                        fontSize: '18px'
                    }}>
                        Project Information
                    </h5>
                    <div className="d-flex gap-2">
                        <ActionButtonGradient
                            onClick={() => navigate(`/project-list/remarks-setting/${params.project_id}`)}
                            variant="primary"
                            children={"Set Remarks"}
                        />
                        <ActionButtonGradient
                            onClick={() => navigate(`/project-list/project-form/${params.project_id}`)}
                            variant="success"
                            children={"Update Project"}
                        />
                    </div>
                </div>
                
                <div style={{ marginBottom: '24px' }}>
                    {/* <div key={index} className="mb-4" style={{
                                padding: '20px',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '8px',
                                border: '1px solid #e5e7eb'
                            }}></div> */}
                    
                    <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: '250px 20px 2fr',
                        gap: '16px',
                        marginBottom: '20px',
                        padding: '20px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb'
                    }}>
                        <div style={{ 
                            display: 'flex', 
                            flexDirection: 'column',
                            gap: '8px'
                        }}>
                            <p style={{ margin: 0, fontWeight: '600', color: '#374151', fontSize: '14px' }}>Name</p>
                            <p style={{ margin: 0, fontWeight: '600', color: '#374151', fontSize: '14px' }}>Year Performance</p>
                            <p style={{ margin: 0, fontWeight: '600', color: '#374151', fontSize: '14px' }}>Status</p>
                            <p style={{ margin: 0, fontWeight: '600', color: '#374151', fontSize: '14px' }}>Calibration Score Exceeds Quota</p>
                            <p style={{ margin: 0, fontWeight: '600', color: '#374151', fontSize: '14px' }}>Remarks Setting</p>
                        </div>
                        <div style={{ 
                            display: 'flex', 
                            flexDirection: 'column',
                            gap: '8px'
                        }}>
                            <p style={{ margin: 0, color: '#374151', fontSize: '14px' }}>:</p>
                            <p style={{ margin: 0, color: '#374151', fontSize: '14px' }}>:</p>
                            <p style={{ margin: 0, color: '#374151', fontSize: '14px' }}>:</p>
                            <p style={{ margin: 0, color: '#374151', fontSize: '14px' }}>:</p>
                            <p style={{ margin: 0, color: '#374151', fontSize: '14px' }}>:</p>
                        </div>
                        <div style={{ 
                            display: 'flex', 
                            flexDirection: 'column',
                            gap: '8px'
                        }}>
                            <p style={{ margin: 0, color: '#1f2937', fontSize: '14px' }}>{payload.Name}</p>
                            <p style={{ margin: 0, color: '#1f2937', fontSize: '14px' }}>{payload.Year}</p>
                            <p style={{ margin: 0, color: '#1f2937', fontSize: '14px' }}>{payload.Active ? "Active" : "Not Active"}</p>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
                                <label style={{ display: 'flex', alignItems: 'center', fontSize: '14px' }}>
                                    <Checkbox disabled name="APlusExcess" checked={payload?.APlusExcess} />
                                    <span style={{ marginLeft: '8px' }}>A+</span>
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', fontSize: '14px' }}>
                                    <Checkbox disabled name="AExcess" checked={payload?.AExcess} style={{ marginLeft: '8px' }} />
                                    <span style={{ marginLeft: '8px' }}>A</span>
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', fontSize: '14px' }}>
                                    <Checkbox disabled name="BPlusExcess" checked={payload?.BPlusExcess} style={{ marginLeft: '8px' }} />
                                    <span style={{ marginLeft: '8px' }}>B+</span>
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', fontSize: '14px' }}>
                                    <Checkbox disabled name="BExcess" checked={payload?.BExcess} style={{ marginLeft: '8px' }} />
                                    <span style={{ marginLeft: '8px' }}>B</span>
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', fontSize: '14px' }}>
                                    <Checkbox disabled name="CExcess" checked={payload?.CExcess} style={{ marginLeft: '8px' }} />
                                    <span style={{ marginLeft: '8px' }}>C</span>
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', fontSize: '14px' }}>
                                    <Checkbox disabled name="DExcess" checked={payload?.DExcess} style={{ marginLeft: '8px' }} />
                                    <span style={{ marginLeft: '8px' }}>D</span>
                                </label>
                            </div>
                            <div>
                                {payload.RemarkSettings?.map((remark, index) => (
                                    <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                        <p style={{ margin: 0, fontSize: '14px', color: '#1f2937' }}>
                                            {remark.JustificationType === "both" && "Top & Bottom Remark"}
                                            {remark.JustificationType === "top" && "Top Remark"}
                                            {remark.JustificationType === "bottom" && "Bottom Remark"}
                                        </p>
                                        {remark.ScoringType === "level" && (
                                            <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>({remark.Level} Level)</p>
                                        )}
                                        {remark.ScoringType === "rating" && (
                                            <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>({remark.From} to {remark.To})</p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                   
                    <div style={{ marginTop: '24px' }}>
                        <h6 style={{ 
                            marginBottom: '16px',
                            fontWeight: '600',
                            color: '#1f2937',
                            fontSize: '18px'
                        }}>
                            Score Distribution
                        </h6>
                        <div style={{ overflowX: 'auto' }}>
                            <table className="table" style={{ backgroundColor: 'white', borderRadius: '8px' }}>
                                <thead>
                                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                                        <th style={{ 
                                            border: '1px solid #dee2e6'
                                         }}></th>
                                        {payload.ScoreDistributions?.map((sc, index) => (
                                            <th key={index} scope="col" style={{ 
                                                textAlign: 'center',
                                                fontWeight: 600, 
                                                color: '#495057', 
                                                padding: '12px',
                                                fontSize: '14px',
                                                border: '1px solid #dee2e6'
                                            }}>
                                                {sc.GroupBusinessUnit.GroupName}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td style={{ 
                                            textAlign: 'center', 
                                            fontWeight: '600',
                                            padding: '12px',
                                            fontSize: '14px',
                                            border: '1px solid #dee2e6',
                                            backgroundColor: '#f8f9fa'
                                        }}>A+</td>
                                        {payload.ScoreDistributions?.map((sc, index) => (
                                            <td key={index} style={{ 
                                                textAlign: 'center',
                                                padding: '12px',
                                                fontSize: '14px',
                                                border: '1px solid #dee2e6'
                                            }}>
                                                {sc.APlusLowerLimit} - {sc.APlusUpperLimit}
                                            </td>
                                        ))}
                                    </tr>
                                    <tr>
                                        <td style={{ 
                                            textAlign: 'center', 
                                            fontWeight: '600',
                                            padding: '12px',
                                            fontSize: '14px',
                                            border: '1px solid #dee2e6',
                                            backgroundColor: '#f8f9fa'
                                        }}>A</td>
                                        {payload.ScoreDistributions?.map((sc, index) => (
                                            <td key={index} style={{ 
                                                textAlign: 'center',
                                                padding: '12px',
                                                fontSize: '14px',
                                                border: '1px solid #dee2e6'
                                            }}>
                                                {sc.ALowerLimit} - {sc.AUpperLimit}
                                            </td>
                                        ))}
                                    </tr>
                                    <tr>
                                        <td style={{ 
                                            textAlign: 'center', 
                                            fontWeight: '600',
                                            padding: '12px',
                                            fontSize: '14px',
                                            border: '1px solid #dee2e6',
                                            backgroundColor: '#f8f9fa'
                                        }}>B+</td>
                                        {payload.ScoreDistributions?.map((sc, index) => (
                                            <td key={index} style={{ 
                                                textAlign: 'center',
                                                padding: '12px',
                                                fontSize: '14px',
                                                border: '1px solid #dee2e6'
                                            }}>
                                                {sc.BPlusLowerLimit} - {sc.BPlusUpperLimit}
                                            </td>
                                        ))}
                                    </tr>
                                    <tr>
                                        <td style={{ 
                                            textAlign: 'center', 
                                            fontWeight: '600',
                                            padding: '12px',
                                            fontSize: '14px',
                                            border: '1px solid #dee2e6',
                                            backgroundColor: '#f8f9fa'
                                        }}>B</td>
                                        {payload.ScoreDistributions?.map((sc, index) => (
                                            <td key={index} style={{ 
                                                textAlign: 'center',
                                                padding: '12px',
                                                fontSize: '14px',
                                                border: '1px solid #dee2e6'
                                            }}>
                                                {sc.BLowerLimit} - {sc.BUpperLimit}
                                            </td>
                                        ))}
                                    </tr>
                                    <tr>
                                        <td style={{ 
                                            textAlign: 'center', 
                                            fontWeight: '600',
                                            padding: '12px',
                                            fontSize: '14px',
                                            border: '1px solid #dee2e6',
                                            backgroundColor: '#f8f9fa'
                                        }}>C</td>
                                        {payload.ScoreDistributions?.map((sc, index) => (
                                            <td key={index} style={{ 
                                                textAlign: 'center',
                                                padding: '12px',
                                                fontSize: '14px',
                                                border: '1px solid #dee2e6'
                                            }}>
                                                {sc.CLowerLimit} - {sc.CUpperLimit}
                                            </td>
                                        ))}
                                    </tr>
                                    <tr>
                                        <td style={{ 
                                            textAlign: 'center', 
                                            fontWeight: '600',
                                            padding: '12px',
                                            fontSize: '14px',
                                            border: '1px solid #dee2e6',
                                            backgroundColor: '#f8f9fa'
                                        }}>D</td>
                                        {payload.ScoreDistributions?.map((sc, index) => (
                                            <td key={index} style={{ 
                                                textAlign: 'center',
                                                padding: '12px',
                                                fontSize: '14px',
                                                border: '1px solid #dee2e6'
                                            }}>
                                                {sc.DLowerLimit} - {sc.DUpperLimit}
                                            </td>
                                        ))}
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
                
                <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid #e5e7eb' }}>
                    <Tabs 
                        onChange={handleTabChange}
                        defaultActiveKey="1"
                        activeKey={activeTabs}
                        tabPosition={"top"}
                        items={tabs}
                    />
                </div>

                <div className="d-flex justify-content-end gap-3" style={{ marginTop: '32px', paddingTop: '24px', borderTop: '1px solid #e5e7eb' }}>
                    {payload.Active ? (
                        <>
                            <ActionButtonGradient
                                onClick={() => notifyCalibrator(params.project_id)}
                                variant="primary"
                                icon={<IoIosNotifications style={{ fontSize: '18px' }} />}
                                disabled={isSendNotification}
                                children={isSendNotification ? "Sending..." : "Notify Calibrators"}
                            />
                            <ActionButtonGradient
                                onClick={() => deactivateProject(params.project_id)}
                                variant="danger"
                                children={"Deactivate Project"}
                            />
                        </>
                    ) : (
                        <ActionButtonGradient
                            onClick={() => publishProject(params.project_id)}
                            variant="success"
                            children={"Publish Project"}
                        />
                    )}
                </div>
            </div>
            
            <DeleteModal 
                show={show} 
                handleClose={handleClose} 
                handleOk={async () => {
                    if (isRatingQuota) {
                        await deleteRatingQuota(deleteID)
                    } else {
                        await deleteCalibrationData(deleteID)
                    }
                    handleClose()
                }} 
                title={deleteTitleName}
            />
            <Loading state={loadingPagestate}/>
        </div>
    )
}

export default ProjectDetail
